<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue'
import type { NavigationItem } from '../../models/navigation/NavigationItems.model'
import AppIcon from '@layer/components/elements/AppIcon.vue'
import { useLocalePath } from '#i18n'

const props = defineProps<{
  item: NavigationItem | null
  isItemActive: (item: NavigationItem) => boolean
}>()

const emit = defineEmits(['close', 'navigate'])

const _localePath = useLocalePath()
const submenuItems = computed(() => props.item?.children || [])
const firstItemRef = ref<HTMLElement | null>(null)
const submenuItemRefs = ref<HTMLElement[]>([])

function handleBackClick(event?: MouseEvent) {
  if (event) {
    event.stopPropagation()
  }
  emit('close')
}

function handleItemClick(subItem: NavigationItem, event?: MouseEvent) {
  if (event) {
    event.stopPropagation()
  }

  if (subItem.to) {
    emit('navigate', subItem)
  }
}

function focusFirstItem() {
  nextTick(() => {
    if (firstItemRef.value) {
      firstItemRef.value.focus()
    } else if (submenuItemRefs.value.length > 0) {
      submenuItemRefs.value[0]?.focus()
    }
  })
}

function setItemRef(el: unknown, index: number) {
  if (el instanceof HTMLElement) {
    if (index === 0) {
      firstItemRef.value = el
    }
    submenuItemRefs.value[index] = el
  } else if (el && typeof el === 'object' && '$el' in el) {
    const vueEl = el as { $el: HTMLElement }
    if (vueEl.$el instanceof HTMLElement) {
      if (index === 0) {
        firstItemRef.value = vueEl.$el
      }
      submenuItemRefs.value[index] = vueEl.$el
    }
  }
}

function handleKeyDown(event: KeyboardEvent, index: number) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (index < submenuItemRefs.value.length - 1) {
        submenuItemRefs.value[index + 1]?.focus()
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (index > 0) {
        submenuItemRefs.value[index - 1]?.focus()
      } else {
        const backButton = document.querySelector(
          '[data-testid="close-submenu-button"]'
        ) as HTMLElement
        if (backButton) backButton.focus()
      }
      break
    case 'Escape':
      event.preventDefault()
      handleBackClick()
      break
    case 'ArrowLeft':
      event.preventDefault()
      handleBackClick()
      break
  }
}

onMounted(() => {
  submenuItemRefs.value = Array(submenuItems.value.length).fill(null)
})

defineExpose({
  focusFirstItem,
})
</script>

<template>
  <Transition
    name="slide"
    enter-active-class="transform transition ease-in-out duration-300"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transform transition ease-in-out duration-300"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <div
      v-if="item"
      class="bg-white fixed inset-0 z-50 flex flex-col"
      :data-item-label="item.label"
      data-testid="mobile-submenu"
      :aria-label="`${item.label} submenu`"
      role="menu"
      aria-orientation="vertical"
      @click.stop
    >
      <!-- Header with back button -->
      <div
        class="flex h-16 items-center border-b border-accent-06 px-4"
        data-testid="mobile-submenu-header"
      >
        <button
          type="button"
          class="flex items-center justify-center rounded-md p-2 text-black hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-black"
          data-testid="close-submenu-button"
          aria-label="Go back to main navigation"
          @click.stop="handleBackClick"
          @keydown.enter="handleBackClick()"
          @keydown.escape="handleBackClick()"
        >
          <AppIcon
            name="heroicons-outline:arrow-left"
            size="sm"
            color="basics-icon"
            aria-hidden="true"
          />
        </button>

        <h2 class="ml-3 text-base font-semibold text-black">
          {{ item.label }}
        </h2>
      </div>

      <!-- Submenu items -->
      <div class="flex-grow overflow-y-auto p-4">
        <div class="space-y-2" role="menu">
          <template v-if="submenuItems.length > 0">
            <button
              v-for="(subItem, index) in submenuItems"
              :key="subItem.label"
              :ref="(el) => setItemRef(el, index)"
              type="button"
              class="flex w-full items-center rounded-md px-3 py-2 text-left text-base text-black hover:bg-primary-50 focus:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-black"
              :class="{ 'font-bold': isItemActive(subItem) }"
              role="menuitem"
              tabindex="0"
              @click.stop="handleItemClick(subItem, $event)"
              @keydown.enter="handleItemClick(subItem)"
              @keydown="(e) => handleKeyDown(e, index)"
            >
              <AppIcon
                :name="subItem.icon || 'heroicons-outline:document'"
                size="sm"
                color="basics-icon"
                class="mr-3"
                aria-hidden="true"
              />
              <span>{{ subItem.label }}</span>
            </button>
          </template>
          <div v-else class="px-3 py-2 text-accent-04">
            No submenu items available
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
